import { Moment } from "moment-timezone";

export interface IIconButtonModal {
  onCancel?: () => void;
}

export type Headers = {
  [key: string]: string;
};

export type AccountType = {
  usr: string;
  imageUrl: string;
  filter: (spaces: SpaceHierarchy[]) => string[] | undefined;
};

export type ConfigurationType = {
  [key: string]: AccountType;
};

export enum Params {
  TAGS = "type",
  SEARCH = "search",
  DATA = "data",
  DATE = "date",
  DASHBOARD = "dashboard",
  IMPERSONATE = "impersonate",
}

export type Setting = {
  [key: string]: string | number | null | boolean;
};

export type Settings = {
  dashboard?: string | number | null | boolean;
  date?: string | null;
  impersonate?: DensityAccount;
  isImpersonating?: boolean | undefined;
  search?: string | null;
  sort?: DataType;
  order?: Sorting;
  capacity?: DataType;
  safe?: DataType;
  busiest?: DataType;
  average?: DataType;
};

export type UserType = Partial<{
  id: string;
  email: string;
  name: string;
  sessionToken: string;
  authToken: string;
  organization: any;
  role: string;
  permissions?: string[];
  isImpersonating: boolean;
  coreReadPermission: boolean;
  coreWritePermission: boolean;
  sensorsListPermission: boolean;
  impersonatePermission: boolean;
  impersonatedOrganizationId: string;
  impersonatedOrganizationName: string;
  impersonatedUserId: string;
  impersonatedUserName: string;
  impersonatedUserEmail: string;
  impersonatedUserRole: string;
}> | null;

export type TagMap = { [key: string]: Tag };

export type SpaceMap = { [key: string]: Space };

export type Tag = {
  title: string;
  category: string;
  number: number;
  selected: boolean;
  disabled: boolean;
  shared: boolean;
};

export enum EditTag {
  ADD = "add",
  DELETE = "delete",
}

export type EditedTag = {
  title: string;
  category: string;
  number: number;
  selected: boolean;
  disabled: boolean;
  shared: boolean;
  edit: EditTag;
};

export type EditedTagMap = { [key: string]: EditedTag };

export enum Error {
  Network = "network",
  Unhandled = "Unhandled",
  Authentication = "Authentication",
}

export enum Dashboard {
  Chart = "chart",
  Table = "table",
}

export enum Check {
  Checked = "checked",
  Unchecked = "unchecked",
  Group = "group",
}

export enum Sorting {
  Incremental = "incremental",
  Decremental = "decremental",
  None = "none",
}

export enum DataType {
  Name = "name",
  TargetCapacity = "target_capacity",
  LegalCapacity = "legal_capacity",
  SafeCapacityPercentage = "safe_capacity_percentage",
  SafeCapacityPpl = "safe_capacity_ppl",
  BusiestPercentage = "busiest_percentage",
  BusiestPpl = "busiest_ppl",
  AveragePercentage = "average_percentage",
  AveragePpl = "average_ppl",
}

export enum SIconType {
  Logo = "logo",
  Dashboard = "dashboard",
  Search = "search",
  Filters = "filters",
  ChevronDown = "chevron-down",
  Blank = "blank",
  Check = "check",
  Error = "error",
  Safe = "Safe",
  Gear = "gear",
  Table = "table",
  Tag = "tag",
  Pen = "pen",
  Chart = "chart",
  Dash = "dash",
  Closed = "closed",
  ArrowDown = "arrow-descriptionown",
  ArrowLeft = "arrow-left",
  ArrowRight = "arrow-right",
  AuthError = "auth-error",
  ServerError = "server-error",
  UnexpectedError = "unexpected-error",
  NetworkError = "network-error",
  Impersonate = "Impersonate",
  World = "World",
  SortIncremental = "SortIncremental",
  SortDecremental = "SortDecremental",
  ErrorNetwork = "ErrorNetwork",
  ErrorAuth = "ErrorAuth",
  ErrorServer = "ErrorServer",
  ErrorUnexpected = "ErrorUnexpected",
  LeftArrow = "LeftArrow",
  RightArrow = "RightArrow",
  Clear = "Clear",
  Number = "Number",
  Percentage = "Percentage",
}

export enum Orientation {
  Vertical = "vertical",
  Horizontal = "horizontal",
}

export enum STagType {
  Closed = "closed",
  Numbered = "numbered",
  Plain = "plain",
  Choice = "choice",
  Expanded = "expanded",
  Edited = "edited",
}

export type Doorway = {
  id: string;
  name: string;
  link_id: string;
  sensor_placement: number;
};

export type Ancestry = {
  id: string;
  name: string;
};

export enum SpaceType {
  SPACE = "space",
  FLOOR = "floor",
  BUILDING = "building",
  CAMPUS = "campus",
}

export type MapType = {
  [key: string]: any;
};

export type Space = {
  id: string;
  name: string;
  notes: string;
  meta: any;
  space_type: SpaceType;
  function: string;
  time_zone: string;
  daily_reset: string;
  current_count: number;
  capacity: any;
  target_capacity: any;
  legal_capacity: any;
  safe_capacity: any;
  safe_threshold_mode: any;
  safe_threshold_value: any;
  max_dwell_minutes: any;
  parent_id: string;
  ancestry: Ancestry[];
  address: string;
  latitude: any;
  longitude: any;
  counting_mode: string;
  component_spaces: any[];
  floor_level: any;
  space_mappings: any[];
  doorways: Doorway[];
  tags: string[];
  time_segments: [any];
  time_segment_groups: [any];
  size_area: any;
  size_area_unit: any;
  annual_rent: any;
  annual_rent_currency: string;
  assigned_teams: any[];
  sensors_total: number;
  image_url: string;
  created_at: string;
  updated_at: string;
  description: string;
  inherits_time_segments: boolean;
  reports: any;
  selected?: boolean;
  filtered?: boolean;
};

export type TimeSegment = {
  id: string;
  label: string;
  start: string;
  end: string;
  days: string[];
};

export type SpaceHierarchy = {
  id: string;
  name: string;
  has_purview: true;
  space_type: string;
  daily_reset: string;
  inherits_time_segments: boolean;
  time_segments: TimeSegment[];
  capacity: number | null;
  children: SpaceHierarchy[] | null;
  address: string;
};

export type DailyAnalytic = {
  count: number;
  interval: {
    analytics: {
      entrances: number;
      entry_rate: number;
      events: number;
      exit_rate: number;
      exits: number;
      max: number;
      min: number;
      target_utilization: number;
      utilization: number;
    };
    end: string;
    start: string;
  };
  timestamp: string;
};

export type DateRange = {
  startDate: Moment | string | number;
  endDate: Moment | string | number;
};

export enum ErrorType {
  NETWORK = "network",
  API_UNAUTHORIZED = "api_unauthorized",
  API_NOT_REACHABLE = "api_not_reachable",
  HANDLED = "handled",
  TIMEOUT = "timeout",
  API_NO_CAMPUS = "api_no_campus",
  AUTH = "Auth",
}

export enum DensityAccount {
  CBRE = "CBRE",
  SPLUNK = "Splunk",
  DELTA = "Delta",
  NETFLIX = "Netflix",
  REKOM = "Rekom",
  BROOKFIELD = "Brookfield",
  DENSITY = "Density",
  VERIZON = "Verizon",
  OKTA = "Okta",
  ACME_INC = "Acme Inc",
  OTHER = "Other",
  DEMO = "Demo",
  DENSITY_DEV = "Density Dev",
  MEDALLIA = "Medallia",
  TWITTER = "Twitter",
  BMS = "BMS",
}

export enum SToggleSelect {
  LEFT = "left",
  RIGHT = "right",
  NONE = "NONE",
}
